
import { useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCampaignSubmission } from "@/hooks/useCampaignSubmission";
import { AutoSync } from "@/components/campaign/AutoSync";
import { useEffect } from "react";
import { useUserStorage } from "@/hooks/campaign/utils/storageUtils";
import { IndexHeader } from "@/components/campaign/IndexHeader";
import { CampaignModals } from "@/components/campaign/CampaignModals";
import { useCampaignDbSave } from "@/components/campaign/hooks/useCampaignDbSave";
import { useRlsSetup } from "@/components/campaign/hooks/useRlsSetup";
import { CampaignLanding } from "@/components/campaign/CampaignLanding";
import { CampaignCreator } from "@/components/campaign/CampaignCreator";

interface CampaignPageProps {
  mode?: 'landing' | 'create' | 'draft' | 'edit';
}

export const CampaignPage = ({ mode = 'landing' }: CampaignPageProps) => {
  const { draftId, campaignId } = useParams();
  const { 
    isSubmitting, 
    showTerms, 
    setShowTerms, 
    showThankYou,
    setShowThankYou,
    formKey, 
    showPayment,
    handleAcceptTerms, 
    handlePaymentComplete, 
    handlePaymentCancel, // Add the new handler
    setShowPayment,
    currentCampaignId,
    hasSubmitted
  } = useCampaignSubmission();
  
  const { getItem, setItem, removeItem } = useUserStorage();
  
  // Setup RLS policies
  useRlsSetup();
  
  // Save campaign data to database
  useCampaignDbSave(showThankYou, currentCampaignId, hasSubmitted);
  
  // Add a direct keyboard shortcut for debugging submission
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt+S shortcut disabled - terms now handled in payment popup
      if (e.altKey && e.key === 's') {
        console.log("CampaignPage: Alt+S pressed, but terms are now handled in payment popup");
        // Terms are now handled within payment popup
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setShowTerms]);

  // Handle thank you popup from localStorage flag
  useEffect(() => {
    const shouldShowThankYou = getItem('showThankYou') === 'true';
    if (shouldShowThankYou) {
      console.log('Showing thank you popup from user-specific localStorage flag');
      setShowThankYou(true);
      removeItem('showThankYou');
    }
  }, [setShowThankYou, getItem, removeItem]);
  
  // Add a direct button click detector that bypasses component hierarchy
  useEffect(() => {
    const handleSubmitButtonClick = (event: MouseEvent) => {
      // Check if the clicked element or its parent is a submit button with specific data attributes
      let target = event.target as HTMLElement;
      
      // Check up to 3 levels deep for a submit button
      for (let i = 0; i < 3; i++) {
        if (!target) break;
        
        const isSubmitButton = 
          target.dataset?.testid === 'step-submit-button' || 
          target.dataset?.testid === 'campaign-submit-button';
        
        if (isSubmitButton && !isSubmitting) {
          console.log("CampaignPage: Submit button click detected directly on DOM element");
          // Prevent the regular click if we're handling it specially
          event.preventDefault();
          event.stopPropagation();
          
          // Open payment popup directly (terms handled within)
          console.log("CampaignPage: Opening payment popup directly from submit button click");
          setShowPayment(true);
          break;
        }
        
        // Move up the DOM tree
        target = target.parentElement as HTMLElement;
      }
    };
    
    document.addEventListener('click', handleSubmitButtonClick, true);
    return () => {
      document.removeEventListener('click', handleSubmitButtonClick, true);
    };
  }, [setShowTerms, isSubmitting]);
  
  return (
    <div className="flex flex-col min-h-[100dvh] bg-background overflow-auto">
      <div className="relative flex-1 w-full max-w-screen-xl mx-auto px-4 sm:px-6">
        <div className="w-full mx-auto py-10 sm:py-16 space-y-6 sm:space-y-8">
          <IndexHeader />
          
          {mode === 'landing' && <CampaignLanding />}
          {(mode === 'create' || mode === 'draft' || mode === 'edit') && <CampaignCreator campaignId={campaignId} mode={mode} />}
        </div>
      </div>

      <CampaignModals 
        showPayment={showPayment}
        showTerms={showTerms}
        showThankYou={showThankYou}
        setShowPayment={setShowPayment}
        setShowTerms={setShowTerms}
        setShowThankYou={setShowThankYou}
        handleAcceptTerms={handleAcceptTerms}
        handlePaymentComplete={handlePaymentComplete}
        handlePaymentCancel={handlePaymentCancel} // Pass the new handler
        isSubmitting={isSubmitting}
        removeItem={removeItem}
      />
      
      <AutoSync />
    </div>
  );
};

export default CampaignPage;
